// @refresh reset
import React, { useState, useEffect, useCallback } from 'react';
import type { Tour, TourState, TourProgress, TourContextType, TourAnalytics } from '@/types/product-tour';
import { getTourById } from '@/config/tours';
import { TourContext } from './TourContextValue';
import { prefixKey } from '@/utils/storagePrefix';

const STORAGE_KEY = prefixKey('timetrack-tour-progress');
const PENDING_TOUR_KEY = prefixKey('timetrack-pending-tour');

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [currentTour, setCurrentTour] = useState<Tour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [tourProgress, setTourProgress] = useState<TourProgress>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tourProgress));
  }, [tourProgress]);

  const trackAnalytics = useCallback((analytics: Omit<TourAnalytics, 'timestamp'>) => {
    const event = {
      ...analytics,
      timestamp: new Date(),
    };
    void event;
  }, []);

  const startTour = useCallback((tourId: string) => {
    const tour = getTourById(tourId);
    if (!tour) {
      console.error(`Tour with id "${tourId}" not found`);
      return;
    }

    setCurrentTour(tour);
    setCurrentStep(0);
    setIsActive(true);

    const newState: TourState = {
      tourId,
      currentStep: 0,
      totalSteps: tour.steps.length,
      status: 'in_progress',
      startedAt: new Date(),
      lastInteraction: new Date(),
      dismissalCount: tourProgress[tourId]?.dismissalCount || 0,
      skippedSteps: [],
    };

    setTourProgress(prev => ({
      ...prev,
      [tourId]: newState,
    }));

    trackAnalytics({
      tourId,
      action: 'started',
      step: 0,
      timeSpent: 0,
    });
  }, [tourProgress, trackAnalytics]);

  const nextStep = useCallback(() => {
    if (!currentTour) return;

    const nextStepIndex = currentStep + 1;

    if (nextStepIndex >= currentTour.steps.length) {
      completeTour();
      return;
    }

    setCurrentStep(nextStepIndex);

    setTourProgress(prev => ({
      ...prev,
      [currentTour.id]: {
        ...prev[currentTour.id],
        currentStep: nextStepIndex,
        lastInteraction: new Date(),
      },
    }));

    trackAnalytics({
      tourId: currentTour.id,
      action: 'step_completed',
      step: currentStep,
    });
  }, [currentTour, currentStep, trackAnalytics]);

  const previousStep = useCallback(() => {
    if (!currentTour || currentStep === 0) return;

    const prevStepIndex = currentStep - 1;
    setCurrentStep(prevStepIndex);

    setTourProgress(prev => ({
      ...prev,
      [currentTour.id]: {
        ...prev[currentTour.id],
        currentStep: prevStepIndex,
        lastInteraction: new Date(),
      },
    }));
  }, [currentTour, currentStep]);

  const skipTour = useCallback(() => {
    if (!currentTour) return;

    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);

    setTourProgress(prev => ({
      ...prev,
      [currentTour.id]: {
        ...prev[currentTour.id],
        status: 'skipped',
        lastInteraction: new Date(),
        skippedSteps: [...(prev[currentTour.id]?.skippedSteps || []), currentStep],
      },
    }));

    trackAnalytics({
      tourId: currentTour.id,
      action: 'tour_skipped',
      step: currentStep,
      completionRate: currentStep / currentTour.steps.length,
    });
  }, [currentTour, currentStep, trackAnalytics]);

  const completeTour = useCallback(() => {
    if (!currentTour) return;

    setIsActive(false);
    const completedTour = currentTour;
    setCurrentTour(null);
    setCurrentStep(0);

    setTourProgress(prev => ({
      ...prev,
      [completedTour.id]: {
        ...prev[completedTour.id],
        status: 'completed',
        completedAt: new Date(),
        lastInteraction: new Date(),
      },
    }));

    trackAnalytics({
      tourId: completedTour.id,
      action: 'tour_completed',
      completionRate: 1,
    });
  }, [currentTour, trackAnalytics]);

  const dismissTour = useCallback(() => {
    if (!currentTour) return;

    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);

    setTourProgress(prev => ({
      ...prev,
      [currentTour.id]: {
        ...prev[currentTour.id],
        status: 'dismissed',
        lastInteraction: new Date(),
        dismissalCount: (prev[currentTour.id]?.dismissalCount || 0) + 1,
      },
    }));

    trackAnalytics({
      tourId: currentTour.id,
      action: 'tour_dismissed',
      step: currentStep,
    });
  }, [currentTour, currentStep, trackAnalytics]);

  const restartTour = useCallback((tourId: string) => {
    setTourProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[tourId];
      return newProgress;
    });

    startTour(tourId);
  }, [startTour]);

  const getTourState = useCallback((tourId: string): TourState | undefined => {
    return tourProgress[tourId];
  }, [tourProgress]);

  const updateTourProgress = useCallback((tourId: string, state: Partial<TourState>) => {
    setTourProgress(prev => ({
      ...prev,
      [tourId]: {
        ...(prev[tourId] || {}),
        ...state,
        tourId,
        lastInteraction: new Date(),
      } as TourState,
    }));
  }, []);

  const shouldShowTour = useCallback((tourId: string): boolean => {
    const state = tourProgress[tourId];

    if (state?.status === 'completed') return false;
    if (state?.dismissalCount && state.dismissalCount >= 3) return false;

    if (state?.status === 'dismissed' && state.lastInteraction) {
      const daysSinceDismissal = (Date.now() - new Date(state.lastInteraction).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal < 7) return false;
    }

    if (state?.status === 'remind_later' && state.lastInteraction) {
      const hoursSinceReminder = (Date.now() - new Date(state.lastInteraction).getTime()) / (1000 * 60 * 60);
      if (hoursSinceReminder < 24) return false;
    }

    return true;
  }, [tourProgress]);

  const setPendingTour = useCallback((tourId: string) => {
    localStorage.setItem(PENDING_TOUR_KEY, tourId);
  }, []);

  const getPendingTour = useCallback((): string | null => {
    return localStorage.getItem(PENDING_TOUR_KEY);
  }, []);

  const clearPendingTour = useCallback(() => {
    localStorage.removeItem(PENDING_TOUR_KEY);
  }, []);

  const startPendingTour = useCallback(() => {
    const pendingTourId = getPendingTour();
    if (pendingTourId) {
      clearPendingTour();
      setTimeout(() => {
        const state = getTourState(pendingTourId);
        if (state?.status === 'completed') {
          restartTour(pendingTourId);
        } else {
          startTour(pendingTourId);
        }
      }, 500);
    }
  }, [getPendingTour, clearPendingTour, getTourState, restartTour, startTour]);

  const value: TourContextType = {
    currentTour,
    currentStep,
    isActive,
    tourProgress,
    startTour,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    dismissTour,
    restartTour,
    getTourState,
    shouldShowTour,
    updateTourProgress,
    setPendingTour,
    getPendingTour,
    clearPendingTour,
    startPendingTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
